/**
 * TA v4.3 — Phase transition interstitial.
 *
 * Shown after the candidate submits Phase N (server already moved them to N+1) and before
 * they begin actively answering Phase N+1. Purely UX — the server clock for the new phase
 * has already started ticking when this screen is shown, so the candidate is told that
 * explicitly. They can click "Begin Phase N+1" to dismiss and enter the runner.
 *
 * The candidate can NOT return to Phase N (server-enforced via `locked=true`).
 */
import React, { useMemo } from "react";
import { ArrowRight, CheckCircle2, Clock, Lock } from "lucide-react";
import { fmtClock, secondsUntil } from "./taHelpers";

const PHASE_DETAILS = {
    1: {
        label: "Phase 1: Rapid Fire",
        wrap_up: "Phase 1 submitted and locked. You won't see those questions again.",
    },
    2: {
        label: "Phase 2: Applied Warfare",
        next_brief: "Multi-part problems. Each question has a chain of response fields — Part B unlocks only once you commit to Part A. Earlier parts become read-only as you move forward. The chain punishes early mistakes.",
        what_changes: [
            "Questions are deeper and take longer — pace yourself across the full 23 minutes.",
            "When you commit a Part, the next Part opens. You can still reference earlier parts but you cannot edit them.",
            "At least one question requires you to say \"I don't know — insufficient data\". That is a legitimate answer.",
        ],
    },
    3: {
        label: "Phase 3: Judgment Under Fire",
        next_brief: "Open-ended judgment calls. No code. You'll write the actual emails or messages you'd send to stakeholders, then grade yourself on the final question.",
        what_changes: [
            "Where a question says \"write the email\" — actually write it. Don't paraphrase what you would say.",
            "Q45 is the metacognition question — looking back across everything, which answer would you change?",
            "There is no single correct answer. We are scoring how you reason under pressure.",
        ],
    },
};


export default function TATransitionScreen({ fromPhase, toPhase, state, onContinue }) {
    const fromDetails = PHASE_DETAILS[fromPhase];
    const toDetails = PHASE_DETAILS[toPhase];
    const toPhaseState = state.ta_phase_state?.[`phase${toPhase}`] || {};
    const secsLeft = useMemo(() => secondsUntil(toPhaseState.deadline_at), [toPhaseState.deadline_at]);
    const mins = Math.round((toPhaseState.duration_seconds || 0) / 60);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12" data-testid={`ta-transition-${fromPhase}-to-${toPhase}`}>
            <div className="max-w-2xl w-full">
                <div className="flex items-center gap-3 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <p className="text-xs uppercase tracking-[0.25em] font-mono text-emerald-300">{fromDetails.label} · submitted</p>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed mb-10 ml-8">{fromDetails.wrap_up}</p>

                <div className="border border-border/40 bg-card p-7 mb-6">
                    <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-gray-500 mb-2">Next</p>
                    <h2 className="text-2xl font-display font-bold text-white mb-4">{toDetails.label}</h2>
                    <p className="text-sm text-gray-300 leading-relaxed mb-6">{toDetails.next_brief}</p>

                    <p className="text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-2">What changes</p>
                    <ul className="space-y-2 mb-6">
                        {toDetails.what_changes.map((line, i) => (
                            <li key={i} className="flex gap-3 text-sm text-gray-300 leading-relaxed">
                                <ArrowRight className="w-3.5 h-3.5 text-gray-500 shrink-0 mt-1" />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="flex items-center gap-3 bg-amber-500/5 border border-amber-500/30 px-4 py-3">
                        <Clock className="w-4 h-4 text-amber-300" />
                        <p className="text-xs text-amber-200 leading-relaxed">
                            <span className="font-semibold">Heads up:</span> the {mins}-minute clock for this phase started the moment Phase {fromPhase} was submitted.
                            {secsLeft < (toPhaseState.duration_seconds || 0) && (
                                <span className="ml-1 font-mono">{fmtClock(secsLeft)} remaining now.</span>
                            )}
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <p className="text-[11px] text-gray-600 font-mono flex items-center gap-2">
                        <Lock className="w-3 h-3" /> Phase {fromPhase} is locked — you cannot return to it.
                    </p>
                    <button
                        onClick={onContinue}
                        className="px-6 py-3 bg-white text-black text-sm font-semibold hover:bg-gray-200 flex items-center gap-2"
                        data-testid={`ta-continue-to-phase-${toPhase}-btn`}
                    >
                        Begin Phase {toPhase} <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
